<template>
  <div class="flex min-h-full flex-col bg-white dark:bg-slate-900">
    <!-- Header with search and filter tabs -->
    <div class="border-b border-gray-200 dark:border-slate-700 px-4 py-4">
      <div class="relative mb-4 w-full">
        <div class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <svg class="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          v-model="searchQuery"
          class="block w-full rounded-xl bg-gray-100 dark:bg-slate-800 py-2.5 pl-10 pr-3 text-sm text-gray-900 dark:text-slate-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary"
          type="text"
          placeholder="Search calls"
        >
      </div>

      <!-- Filter tabs: status filters + date filter pill -->
      <div class="no-scrollbar flex items-center gap-2 overflow-x-auto pb-1">
        <button
          v-for="f in statusFilters"
          :key="f.id"
          class="shrink-0 rounded-full px-4 py-1.5 text-sm font-medium transition-colors"
          :class="activeFilter === f.id ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-slate-400 hover:bg-gray-200'"
          @click="activeFilter = f.id"
        >
          {{ f.label }}
        </button>

        <!-- Date filter pill — split into label (opens modal) + × (clears) when active -->
        <template v-if="dateFilter">
          <div class="flex shrink-0 items-stretch overflow-hidden rounded-full bg-primary text-sm font-medium text-white">
            <button
              class="py-1.5 pl-4 pr-3 transition-colors hover:opacity-90"
              @click="openDateModal"
            >
              {{ formatDateFilter(dateFilter) }}
            </button>
            <button
              class="border-l border-white/20 px-2 py-1.5 transition-colors hover:bg-white/20"
              aria-label="Clear date filter"
              @click="clearDateFilter"
            >
              ×
            </button>
          </div>
        </template>
        <template v-else>
          <button
            class="shrink-0 rounded-full bg-gray-100 dark:bg-slate-800 px-4 py-1.5 text-sm font-medium text-gray-600 dark:text-slate-400 transition-colors hover:bg-gray-200"
            @click="openDateModal"
          >
            Date
          </button>
        </template>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading && records.length === 0" class="flex flex-1 items-center justify-center py-12">
      <div class="h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
    </div>

    <!-- Empty State -->
    <div v-else-if="records.length === 0 && !isLoadingMore" class="flex flex-1 flex-col items-center justify-center px-6 py-12 text-center">
      <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800 text-gray-400">
        <svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      </div>
      <h3 class="mb-1 text-lg font-medium text-gray-900 dark:text-slate-100">No calls found</h3>
      <p class="max-w-sm text-sm text-gray-500 dark:text-slate-400">No call records match your current filters.</p>
    </div>

    <!-- Calls List -->
    <div v-else class="flex-1 px-4 py-2">
      <div v-if="filteredGroupedCalls.length === 0" class="flex flex-col items-center justify-center px-2 py-12 text-center">
        <div class="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-slate-800 text-gray-400">
          <svg class="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </div>
        <h3 class="mb-1 text-lg font-medium text-gray-900 dark:text-slate-100">No matching calls loaded yet</h3>
        <p class="max-w-sm text-sm text-gray-500 dark:text-slate-400">
          {{ hasMore ? 'Try loading older call records for more matches.' : 'No call records match your current filters.' }}
        </p>
        <button
          v-if="hasMore"
          type="button"
          class="mt-4 rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
          :disabled="isLoadingMore"
          @click="handleExplicitLoadMore"
        >
          {{ isLoadingMore ? 'Loading...' : 'Load older calls' }}
        </button>
      </div>

      <template v-else>
      <div v-for="group in filteredGroupedCalls" :key="group.dateLabel" class="mb-4">
        <h3 class="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-slate-400">{{ group.dateLabel }}</h3>
        <template v-for="call in group.calls" :key="call.id">
          <ExpandableCallCard
            :call="call"
            :expanded="expandedCallId === call.id"
            @toggle="toggleExpandedCall"
            @details="handleAction('details', call)"
            @call="handleAction('call', call)"
            @summary="handleAction('summary', call)"
            @recording="handleAction('recording', call)"
          />
        </template>
      </div>

      <!-- Loading More Indicator -->
      <div v-if="isLoadingMore" class="flex justify-center py-4">
        <div class="h-6 w-6 animate-spin rounded-full border-b-2 border-primary" />
      </div>

      <div v-if="hasMore && filteredGroupedCalls.length > 0" class="flex justify-center py-4">
        <button
          type="button"
          class="rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 transition hover:bg-gray-50 dark:hover:bg-slate-800"
          :disabled="isLoadingMore"
          @click="handleExplicitLoadMore"
        >
          {{ isLoadingMore ? 'Loading...' : 'Load older calls' }}
        </button>
      </div>

      <div ref="loadMoreSentinel" class="h-2 w-full" />

      </template>
    </div>

    <!-- Date filter modal: year (required), month + day (optional, matching mobile DateFilter) -->
    <Teleport to="body">
      <div
        v-if="showDateModal"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
        @click.self="showDateModal = false"
      >
        <div class="w-80 rounded-2xl bg-white dark:bg-slate-900 p-6 shadow-xl">
          <h3 class="mb-4 text-base font-semibold text-gray-900 dark:text-slate-100">Filter by Date</h3>

          <!-- Year (required) -->
          <div class="mb-3">
            <label class="mb-1 block text-xs font-medium text-gray-600 dark:text-slate-400">Year</label>
            <select
              v-model="modalYear"
              class="w-full rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2 text-sm text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary"
              @change="modalDay = null"
            >
              <option v-for="y in yearOptions" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>

          <!-- Month (optional) -->
          <div class="mb-3">
            <label class="mb-1 block text-xs font-medium text-gray-600 dark:text-slate-400">Month <span class="font-normal text-gray-400">(optional)</span></label>
            <select
              v-model="modalMonth"
              class="w-full rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2 text-sm text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary"
              @change="modalDay = null"
            >
              <option :value="null">Any</option>
              <option v-for="m in monthOptions" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
          </div>

          <!-- Day (optional, disabled until a month is chosen) -->
          <div class="mb-5">
            <label class="mb-1 block text-xs font-medium text-gray-600 dark:text-slate-400">Day <span class="font-normal text-gray-400">(optional)</span></label>
            <select
              v-model="modalDay"
              class="w-full rounded-lg border border-gray-200 dark:border-slate-700 px-3 py-2 text-sm text-gray-900 dark:text-slate-100 focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
              :disabled="modalMonth === null"
            >
              <option :value="null">Any</option>
              <option v-for="d in dayOptions" :key="d" :value="d">{{ d }}</option>
            </select>
          </div>

          <div class="flex justify-end gap-2">
            <button
              class="rounded-xl px-4 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 transition-colors hover:bg-gray-100 dark:hover:bg-slate-700"
              @click="showDateModal = false"
            >
              Cancel
            </button>
            <button
              class="rounded-xl bg-primary px-4 py-2 text-sm font-medium text-white transition hover:opacity-90"
              @click="applyDateFilter"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </Teleport>

    <!-- Action sheets / dialogs — each uses Teleport internally -->
    <CallDetailsSheet
      :show="activeSheet?.type === 'details'"
      :call="activeSheet?.type === 'details' ? activeSheet.call : null"
      @close="activeSheet = null"
    />
    <CallSummarySheet
      :show="activeSheet?.type === 'summary'"
      :call="activeSheet?.type === 'summary' ? activeSheet.call : null"
      @close="activeSheet = null"
    />
    <CallRecordingSheet
      :show="activeSheet?.type === 'recording'"
      :call="activeSheet?.type === 'recording' ? activeSheet.call : null"
      @close="activeSheet = null"
    />
    <CallMobileOnlyDialog
      :show="activeSheet?.type === 'call'"
      :call="activeSheet?.type === 'call' ? activeSheet.call : null"
      @close="activeSheet = null"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onBeforeUnmount, onMounted, watch } from "vue";
import type { Auth } from "firebase/auth";
import type { Database } from "firebase/database";
import { useUserStore } from "~/stores/userStore";
import { useContactStore } from "~/stores/contactStore";
import { fetchCallRecordsPage } from "~/services/calls";
import ExpandableCallCard from "~/components/calls/ExpandableCallCard.vue";
import CallDetailsSheet from "~/components/calls/CallDetailsSheet.vue";
import CallSummarySheet from "~/components/calls/CallSummarySheet.vue";
import CallRecordingSheet from "~/components/calls/CallRecordingSheet.vue";
import CallMobileOnlyDialog from "~/components/calls/CallMobileOnlyDialog.vue";
import type { CallRecord } from "~/types/calls";
import { groupCallRecordsByDate, isCallAnswered, isCallInbound, isCallMissed, isCallOutbound } from "~/utils/calls";
import { useNavbarInfo } from "~/stores/useNabvarInfo";

definePageMeta({
  layout: "default",
});

useHead({ title: "Calls" });

const navbarStore = useNavbarInfo();
navbarStore.changeInfo("Calls", [
  { title: "Call History", link: "/calls" },
  { title: "Scheduled Calls", link: "/scheduled-calls" },
]);

const userStore = useUserStore();
const contactStore = useContactStore();
const userId = computed(() => userStore.userDetails?.userId);

const { $auth, $rtdb } = useNuxtApp();

const records = ref<CallRecord[]>([]);
const hasMore = ref(true);
const isLoading = ref(true);
const isLoadingMore = ref(false);
const loadMoreSentinel = ref<HTMLElement | null>(null);

const searchQuery = ref('');
const activeFilter = ref('all');

// Date filter — mirrors mobile's DateFilter (year required, month + day optional)
interface DateFilter { year: number; month: number | null; day: number | null }
const dateFilter = ref<DateFilter | null>(null);
const showDateModal = ref(false);
const modalYear = ref(new Date().getFullYear());
const modalMonth = ref<number | null>(null);
const modalDay = ref<number | null>(null);

const pageSize = 50;
const hasUserScrolled = ref(false);
const expandedCallId = ref<string | null>(null);

let loadMoreObserver: IntersectionObserver | null = null;

const statusFilters = [
  { id: 'all', label: 'All' },
  { id: 'answered', label: 'Answered' },
  { id: 'missed', label: 'Missed' },
  { id: 'outgoing', label: 'Outgoing' },
];

const filteredRecords = computed(() => {
  let filtered = records.value;

  if (activeFilter.value === 'missed') {
    filtered = filtered.filter((call) => isCallMissed(call));
  } else if (activeFilter.value === 'answered') {
    filtered = filtered.filter((call) => isCallAnswered(call) && isCallInbound(call));
  } else if (activeFilter.value === 'outgoing') {
    filtered = filtered.filter((call) => isCallOutbound(call));
  }

  // Date filter is independent of status filter (both can be active simultaneously)
  if (dateFilter.value) {
    const df = dateFilter.value;
    filtered = filtered.filter((call) => {
      const d = new Date(call.startEpoch * 1000);
      if (d.getFullYear() !== df.year) return false;
      if (df.month !== null && d.getMonth() + 1 !== df.month) return false;
      if (df.month !== null && df.day !== null && d.getDate() !== df.day) return false;
      return true;
    });
  }

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase().trim();
    filtered = filtered.filter((call) => {
      const num = getDisplayNumber(call).toLowerCase();
      return num.includes(q) || (call.did && call.did.toLowerCase().includes(q));
    });
  }

  return filtered;
});

const loadPage = async () => {
  if (!userId.value || !hasMore.value || isLoadingMore.value) return;
  isLoadingMore.value = true;
  
  try {
    const endBeforeEpoch = records.value.at(-1)?.startEpoch;

    const newRecords = await fetchCallRecordsPage(userId.value, endBeforeEpoch, pageSize);
    
    if (newRecords.length < pageSize) {
      hasMore.value = false;
    }
    
    // De-duplicate in case of epoch overlaps (sometimes RTDB endAt is inclusive)
    const existingIds = new Set(records.value.map(r => r.id));
    const uniqueNews = newRecords.filter(r => !existingIds.has(r.id));

    if (uniqueNews.length > 0) {
      records.value = [...records.value, ...uniqueNews];
    }
  } catch (error) {
    console.error("Failed to fetch page", error);
  } finally {
    isLoadingMore.value = false;
    isLoading.value = false;
  }
};

const handleExplicitLoadMore = async () => {
  hasUserScrolled.value = true;
  await loadPage();
};

const getDisplayNumber = (call: CallRecord) => {
  const number = isCallInbound(call)
    ? (call.callerNumber || call.callerId || '')
    : (call.destinationNumber || '');

  if (number) {
    const contactName = contactStore.getNameForNumber(number);
    if (contactName) return contactName;
  }

  return number || 'Unknown';
};

// Year options: current year back 5 years (matching mobile)
const yearOptions = computed(() => {
  const cur = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, i) => cur - i);
});

const monthOptions = [
  { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' }, { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
];

// Day options update dynamically based on selected year + month in the modal
const dayOptions = computed(() => {
  if (modalMonth.value === null) return Array.from({ length: 31 }, (_, i) => i + 1);
  // new Date(y, m, 0) gives the last day of month m (1-indexed) in year y
  const daysInMonth = new Date(modalYear.value, modalMonth.value, 0).getDate();
  return Array.from({ length: daysInMonth }, (_, i) => i + 1);
});

const openDateModal = () => {
  // Pre-populate from current active filter, or default to current year
  if (dateFilter.value) {
    modalYear.value = dateFilter.value.year;
    modalMonth.value = dateFilter.value.month;
    modalDay.value = dateFilter.value.day;
  } else {
    modalYear.value = new Date().getFullYear();
    modalMonth.value = null;
    modalDay.value = null;
  }
  showDateModal.value = true;
};

const applyDateFilter = () => {
  dateFilter.value = {
    year: modalYear.value,
    month: modalMonth.value,
    // Day only applies when a month is also selected
    day: modalMonth.value !== null ? modalDay.value : null,
  };
  showDateModal.value = false;
};

const clearDateFilter = () => { dateFilter.value = null; };

const formatDateFilter = (df: DateFilter | null): string => {
  if (!df) return 'Date';
  const names = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  if (df.month === null) return `${df.year}`;
  if (df.day === null) return `${names[df.month - 1]} ${df.year}`;
  return `${names[df.month - 1]} ${df.day}, ${df.year}`;
};

const filteredGroupedCalls = computed(() => {
  return groupCallRecordsByDate(filteredRecords.value);
});

// Tracks which sheet/dialog is currently open and for which call
type ActiveSheet =
  | { type: 'details'; call: CallRecord }
  | { type: 'summary'; call: CallRecord }
  | { type: 'recording'; call: CallRecord }
  | { type: 'call'; call: CallRecord }
  | null;

const activeSheet = ref<ActiveSheet>(null);

const toggleExpandedCall = (callId: string) => {
  expandedCallId.value = expandedCallId.value === callId ? null : callId;
};

const handleAction = (action: string, call: CallRecord) => {
  if (action === 'details') activeSheet.value = { type: 'details', call };
  else if (action === 'summary') activeSheet.value = { type: 'summary', call };
  else if (action === 'recording') activeSheet.value = { type: 'recording', call };
  else if (action === 'call') activeSheet.value = { type: 'call', call };
};

const handleWindowScroll = () => {
  hasUserScrolled.value = true;
};

const disconnectLoadMoreObserver = () => {
  if (loadMoreObserver) {
    loadMoreObserver.disconnect();
    loadMoreObserver = null;
  }
};

const initLoadMoreObserver = async () => {
  await nextTick();

  if (!import.meta.client || !loadMoreSentinel.value) {
    return;
  }

  disconnectLoadMoreObserver();

  loadMoreObserver = new IntersectionObserver(
    (entries) => {
      if (!hasUserScrolled.value) {
        return;
      }

      if (entries.some((entry) => entry.isIntersecting)) {
        void loadPage();
      }
    },
    {
      root: null,
      rootMargin: '240px 0px',
    }
  );

  loadMoreObserver.observe(loadMoreSentinel.value);
};

onMounted(async () => {
  if (import.meta.client) {
    window.addEventListener('scroll', handleWindowScroll, { passive: true });
  }

  // Initialize contact store for name resolution
  const currentUser = ($auth as Auth | null)?.currentUser;
  if (currentUser) {
    contactStore.initialize(currentUser.uid, $rtdb as Database).catch(console.error);
  }

  if (userId.value) {
    await loadPage();
    await initLoadMoreObserver();
  }
});

watch(userId, async (newVal, oldVal) => {
  if (!newVal) {
    return;
  }

  if (newVal !== oldVal) {
    records.value = [];
    hasMore.value = true;
    isLoading.value = true;
  }

  if (records.value.length === 0) {
    await loadPage();
    await initLoadMoreObserver();
  }
});

watch([searchQuery, activeFilter, dateFilter], async () => {
  // Collapse expanded card when filters/search change to avoid stale open state.
  expandedCallId.value = null;
  await initLoadMoreObserver();
});

watch(filteredRecords, (newRecords) => {
  if (!expandedCallId.value) {
    return;
  }

  const isStillVisible = newRecords.some((call) => call.id === expandedCallId.value);
  if (!isStillVisible) {
    expandedCallId.value = null;
  }
});

onBeforeUnmount(() => {
  disconnectLoadMoreObserver();

  if (import.meta.client) {
    window.removeEventListener('scroll', handleWindowScroll);
  }
});
</script>
